/**
 * COMPANY CASES (/company-cases) — the filter bar's pure rules: per-id counts,
 * the dropdown options, the chip badge, the selected item, when a selection has
 * gone stale, the "assigned to" people, and the `mine/total` label.
 * `components/CasesFilterBar.vue` wires these to the store. Extracted in the
 * /company-cases refactor (2026-10-05), behaviour unchanged.
 */

/** "mine/total" when the viewed user attends only SOME of the cases; else "total". */
export function fractionLabel(total, mine) {
  return mine != null && mine < total ? `${mine}/${total}` : String(total)
}

/** Per-id count over cases — each case counts once per id; null ids skipped. */
export function countIdsOver(cases, idsForCase) {
  const map = new Map()
  for (const c of cases) {
    const seen = new Set()
    for (const id of idsForCase(c)) {
      if (id == null) continue
      const key = String(id)
      if (seen.has(key)) continue
      seen.add(key)
      map.set(key, (map.get(key) ?? 0) + 1)
    }
  }

  return map
}

/** Catalog items present in the counted set, in catalog order. */
export function catalogDropdown(catalog, countById) {
  return catalog.filter(item => (countById.get(String(item.id)) ?? 0) > 0)
}

/**
 * The chip badge for a catalog filter's value: 0 when off; the `any` tally;
 * the `untagged` (none-of) tally when the filter has a none-predicate; 0 for
 * any other sentinel (the caller counts those); else the id's count.
 */
export function catalogFilterCount(value, { cases, countById, anyPredicate, nonePredicate, nonIdValues }) {
  if (value == null) return 0
  if (value === 'any') return cases.filter(anyPredicate).length
  if (value === 'untagged' && nonePredicate) return cases.filter(nonePredicate).length
  if (nonIdValues.includes(value)) return 0

  return countById.get(String(value)) ?? 0
}

/** The selected catalog item (drives the chip's icon / color); null when off or `any`. */
export function selectedCatalogItem(value, catalog) {
  if (value == null || value === 'any') return null

  return catalog.find(item => String(item.id) === String(value)) ?? null
}

/**
 * Should a selected id be dropped? Only when it's gone from EVERY active case
 * (deleted / unlinked) — not merely absent from the current day, so the filter
 * survives moving between days. Never while the list is empty (a refetch) and
 * never for a sentinel.
 */
export function catalogSelectionGone(value, activeCases, idsForCase, nonIdValues) {
  if (value == null || nonIdValues.includes(value)) return false
  if (activeCases.length === 0) return false

  return !activeCases.some(c => idsForCase(c).some(id => String(id) === String(value)))
}

/**
 * The people attending the loaded cases, as filter entries
 * `{ id: 'assignee-<userId>', label, color }`, by label. Their label falls back
 * label → short name → name → "User #<id>".
 */
export function assigneesCatalogOf(cases) {
  const byId = new Map()
  for (const c of cases ?? []) {
    const u = c.assignedTo
    if (u?.id == null) continue
    const key = `assignee-${u.id}`
    if (!byId.has(key)) {
      byId.set(key, { id: key, label: u.label || u.shortName || u.name || `User #${u.id}`, color: u.color || null })
    }
  }

  return Array.from(byId.values()).sort((a, b) => (a.label || '').localeCompare(b.label || ''))
}

/** A case's insurance ids. */
export const insuranceIdsOfCase = c => (c.caseInsurances ?? []).map(ci => ci.insurance?.id)

/**
 * The stage filter's ids for a case: its tag ids AND its `assignee-<id>`
 * sentinel, so picking either family matches and a selected person isn't
 * auto-cleared.
 */
export const stageFilterIdsOfCase = c => [
  ...(c.tags ?? []).map(t => t.tag?.id),
  ...(c.assignedTo?.id != null ? [`assignee-${c.assignedTo.id}`] : []),
]

/** The stage chip's badge: the sentinels' own tallies, else the id's count. */
export function stageChipCount(value, { untagged, unassigned, countById }) {
  if (value == null) return 0
  if (value === 'untagged') return untagged
  if (value === 'unassigned') return unassigned

  return countById.get(String(value)) ?? 0
}
